"use client";

import { useState, useCallback, useMemo } from "react";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import Button from "@components/atoms/Button";
import CarGallery from "@components/molecules/CarGallery";
import { CarGalleryModalProps } from "./CarGalleryModal.types";
import styles from "./CarGalleryModal.module.scss";
import { toKebabCase } from "@utils/stringFormatters";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import dynamic from "next/dynamic";
import { useIsMobile } from "@hooks/use-mobile";
import LoadingSkeleton from "../LoadingSkeleton";
import { getDeterministicBackgroundImage } from "@utils/deterministicBackgroundImage";

const ImageResponsiveAssets = dynamic(
  () => import("../ImageResponsiveAssets"),
  {
    ssr: false,
  }
);
const ImageResponsive = dynamic(() => import("../ImageResponsive"), {
  ssr: false,
  loading: () => <LoadingSkeleton type="card" />,
});

const backgroundImages = [
  "buyCarCardBG01",
  "buyCarCardBG02",
  "buyCarCardBG05",
  "buyCarCardBG06",
  "buyCarCardBG07",
];

const CarGalleryModal = ({
  brand_name,
  model_name,
  name,
  gallery,
  className = "",
  modalOverlayClassName,
  cityName,
  status,
  pageName,
  handleRouteClick,
  imagePriority = false,
}: CarGalleryModalProps) => {
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState<boolean>(false);

  const selectedBackgroundImage = useMemo(() => {
    return getDeterministicBackgroundImage(
      `${brand_name || ""}-${model_name || ""}-${name || ""}`,
      backgroundImages
    );
  }, [brand_name, model_name, name]);

  const handleGalleryOpen = useCallback(async () => {
    setIsGalleryModalOpen(true);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_image",
        car_availability: status === "sellable" ? "Yes" : "No",
        vehicle_make: brand_name,
        vehicle_model: model_name,
        city_name: cityName,
      },
    });
  }, []);

  const handleGalleryClose = useCallback(() => {
    setIsGalleryModalOpen(false);
  }, []);

  return (
    <>
      <div className={`${styles.carImageContainer} ${className}`.trim()}>
        <ImageResponsiveAssets
          name={selectedBackgroundImage}
          alt="Car background"
          fill
          className={styles.backgroundCarImage}
          onClick={handleRouteClick}
          filePath={`/images/background-images`}
          responsiveWidth={400}
          width={useIsMobile() ? 640 : 1280}
        />
        <ImageResponsive
          filePath={`${toKebabCase(brand_name)}/${toKebabCase(model_name)}`}
          imageTag={`Hero-Transparent`}
          alt={name}
          fill
          className={styles.carImage}
          onClickHandler={handleRouteClick}
          responsiveWidth={400}
          priority={imagePriority}
          width={useIsMobile() ? 640 : 1280}
        />
      </div>

      {isGalleryModalOpen && gallery.length > 0 && (
        <CarGallery
          isOpen={isGalleryModalOpen}
          galleryImages={gallery}
          className=""
          preventScroll={true}
          onClose={handleGalleryClose}
        />
      )}
    </>
  );
};

export default CarGalleryModal;
